import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import postSchedule from 'Api/schedule/postSchedule';
import { useModal } from 'Store/modal';
import { useScheduleState } from 'View/schedule/state';

import upsertSchedulePayload from './model/request/upsertSchedulePayload';

const usePostSchedule = ({ resetFormData }) => {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const { filterState } = useScheduleState();
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { onOpenModal, onResetModal } = useModal();
  const { updateState } = useScheduleState();

  return useMutation({
    mutationFn: async (formData) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = upsertSchedulePayload(formData);

      return await postSchedule({ payload });
    },
    onSuccess: () => {
      onOpenToast({
        status: 'success',
        description: t('postScheduleSuccess'),
        duration: 5,
        alwaysShow: false,
      });
      resetFormData();
      updateState({ isOpenFormUpsert: false, initialSelectedSchedule: {} });
      queryClient.invalidateQueries({ queryKey: ['schedules', filterState] });
    },
    onError: (error) => {
      const { response } = error;

      console.log(error);

      if (response.status === 400) {
        onOpenModal({
          isOpen: true,
          type: 'error',
          title: t('postScheduleFailedTitle'),
          contentTitle: t('postScheduleFailedConflictTitle'),
          contentDescription: t('postScheduleFailedConflictDesc'),
          showPrimaryButton: true,
          primaryButtonText: t('postScheduleFailedConflictPrimary'),
          showSecondaryButton: true,
          secondaryButtonText: t('postScheduleFailedConflictSecondary'),
          onClickSecondaryButton: () => {
            resetFormData();
            updateState({ isOpenFormUpsert: false, initialSelectedSchedule: {} });
            onResetModal();
          },
          onClickPrimaryButton: () => {
            onResetModal();
          },
        });
      } else {
        onOpenToast({
          status: 'error',
          description: t('postScheduleFailedServerDesc'),
          duration: 5,
          alwaysShow: false,
        });
      }
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePostSchedule;
